import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  CircularProgress,
  Alert,
  Snackbar,
  Tooltip,
  IconButton,
} from '@mui/material';
import { DataGrid, GridColDef, GridRowId, GridRowsProp, GridRowSelectionModel, GridCallbackDetails } from '@mui/x-data-grid';
import {
  CheckCircle as ConfirmIcon,
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { API_CONFIG } from '../../config/api';

interface DocumentExtraction {
  id: number;
  original_filename: string;
  extracted_data: any; // JSON payload with categories/years/values
}

interface ConfirmPayload {
  hotelId: string;
  fileId: number;
  category: string;
  itemName: string;
  year: number;
  value: number;
}

const ExtractionReviewPage: React.FC = () => {
  const { hotelId, fileId } = useParams<{ hotelId: string; fileId: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<GridRowsProp>([]);
  const [selectionModel, setSelectionModel] = useState<GridRowId[]>([]);
  const [confirming, setConfirming] = useState(false);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const API_BASE = API_CONFIG.BASE_URL;

  const fetchExtraction = useCallback(async () => {
    if (!hotelId) return;
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/analysis/hotels/${hotelId}/documents`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const docs: DocumentExtraction[] = await res.json();

      const doc = docs.find((d) => String(d.id) === String(fileId));
      if (!doc) throw new Error('Document not found');
      if (!doc.extracted_data) throw new Error('No extracted data found for this document');

      // Flatten extracted_data into rows
      const extracted = typeof doc.extracted_data === 'string' ? JSON.parse(doc.extracted_data) : doc.extracted_data;

      const tempRows: any[] = [];
      let idCounter = 1;
      if (Array.isArray(extracted)) {
        // already array of metrics
        extracted.forEach((m: any) => {
          tempRows.push({ id: idCounter++, ...m });
        });
      } else if (extracted.data && extracted.years && extracted.categories) {
        const { data, years, categories } = extracted;
        categories.forEach((cat: any) => {
          years.forEach((yr: string | number) => {
            const val = data[cat.id || cat] ? data[cat.id || cat][yr] : undefined;
            if (val !== undefined) {
              tempRows.push({
                id: idCounter++,
                category: cat.name || cat,
                itemName: cat.item_name || cat.name || cat,
                year: Number(yr),
                value: val,
              });
            }
          });
        });
      } else if (extracted.metrics) {
        extracted.metrics.forEach((m: any) => tempRows.push({ id: idCounter++, ...m }));
      }

      setRows(tempRows);
    } catch (err: any) {
      setError(err.message || 'Failed to load extraction');
    } finally {
      setLoading(false);
    }
  }, [hotelId, fileId]);

  useEffect(() => {
    fetchExtraction();
  }, [fetchExtraction]);

  const handleConfirm = async () => {
    if (!hotelId || selectionModel.length === 0) return;
    setConfirming(true);
    try {
      const token = localStorage.getItem('token');
      const selectedRows = rows.filter((r) => selectionModel.includes(r.id));
      for (const r of selectedRows) {
        const payload: ConfirmPayload = {
          hotelId,
          fileId: Number(fileId),
          category: (r as any).category || (r as any).itemName,
          itemName: (r as any).itemName || (r as any).category,
          year: (r as any).year,
          value: Number((r as any).value),
        };
        await fetch(`${API_BASE}/api/financials/confirm`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(payload),
        });
      }
      setSuccessMsg('Financial data confirmed successfully');
      setSelectionModel([]);
    } catch (err: any) {
      setError(err.message || 'Confirmation failed');
    } finally {
      setConfirming(false);
    }
  };

  const columns: GridColDef[] = [
    { field: 'category', headerName: 'Category', flex: 1, editable: true },
    { field: 'year', headerName: 'Year', width: 110, editable: true, type: 'number' },
    { field: 'value', headerName: 'Value', flex: 1, editable: true, type: 'number' },
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <IconButton onClick={() => navigate(-1)}><ArrowBackIcon /></IconButton>
        <Typography variant="h5" fontWeight={700}>Extraction Review</Typography>
        <Tooltip title="Refresh"><IconButton onClick={fetchExtraction}><RefreshIcon /></IconButton></Tooltip>
      </Box>

      <Paper sx={{ height: 500 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          checkboxSelection
          onRowSelectionModelChange={(newModel: GridRowSelectionModel, _details: GridCallbackDetails) => setSelectionModel([...newModel])}
          disableRowSelectionOnClick
          processRowUpdate={(newRow) => {
            setRows((prev) => prev.map((r) => (r.id === newRow.id ? newRow : r)));
            return newRow;
          }}
        />
      </Paper>

      <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          startIcon={<ConfirmIcon />}
          disabled={selectionModel.length === 0 || confirming}
          onClick={handleConfirm}
        >
          {confirming ? 'Confirming...' : `Confirm (${selectionModel.length})`}
        </Button>
      </Box>

      <Snackbar
        open={!!successMsg}
        autoHideDuration={3000}
        onClose={() => setSuccessMsg(null)}
        message={successMsg}
      />
    </Box>
  );
};

export default ExtractionReviewPage;